import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import type { IntlFormatters } from 'react-intl'

export type AssessmentLevelType = 'excellent' | 'good' | 'fair' | 'poor' | 'veryPoor'

export const baselineAssessmentThresholds = [0, 20, 40, 60, 80, 100]

export interface AssessmentLevelConfig {
  label: string
  dotColor: string
  bgColor: string
  textColor?: string
}

export const getAssessmentLevelConfig = (
  theme: Theme,
  formatMessage?: IntlFormatters['formatMessage'],
): Record<AssessmentLevelType, AssessmentLevelConfig> => ({
  excellent: {
    label: formatMessage?.({ id: 'assessments.Intervals.5.level5' }) || 'Excellent',
    dotColor: theme.palette.brandDecorative.jade,
    bgColor: alpha(theme.palette.brandDecorative.jade, 0.12),
    textColor: theme.palette.text.primary,
  },
  good: {
    label: formatMessage?.({ id: 'assessments.Intervals.5.level4' }) || 'Good',
    dotColor: theme.palette.success.dark,
    bgColor: alpha(theme.palette.success.dark, 0.2),
    textColor: theme.palette.text.primary,
  },
  fair: {
    label: formatMessage?.({ id: 'assessments.Intervals.5.level3' }) || 'Fair',
    dotColor: theme.palette.warning.dark,
    bgColor: alpha(theme.palette.warning.dark, 0.2),
    textColor: theme.palette.text.primary,
  },
  poor: {
    label: formatMessage?.({ id: 'assessments.Intervals.5.level2' }) || 'Poor',
    dotColor: theme.palette.error.dark,
    bgColor: alpha(theme.palette.error.dark, 0.12),
    textColor: theme.palette.text.primary,
  },
  veryPoor: {
    label: formatMessage?.({ id: 'assessments.Intervals.5.level1' }) || 'Very poor',
    dotColor: theme.palette.semantic.error,
    bgColor: alpha(theme.palette.semantic.error, 0.1),
    textColor: theme.palette.text.primary,
  },
})

export const valueToLevelType = (value?: number, levelsCount = 5): AssessmentLevelType => {
  if (!value) return 'veryPoor'

  if (levelsCount === 3) {
    if (value === 3) return 'good'
    if (value === 2) return 'fair'
    return 'poor'
  }

  if (value === 5) return 'excellent'
  if (value === 4) return 'good'
  if (value === 3) return 'fair'
  if (value === 2) return 'poor'
  return 'veryPoor'
}

export const levelTypeToValue = (levelType: AssessmentLevelType, levelsCount = 5): number => {
  if (levelsCount === 3) {
    switch (levelType) {
      case 'good':
        return 3
      case 'fair':
        return 2
      case 'poor':
        return 1
      default:
        return 1
    }
  }

  switch (levelType) {
    case 'excellent':
      return 5
    case 'good':
      return 4
    case 'fair':
      return 3
    case 'poor':
      return 2
    case 'veryPoor':
      return 1
    default:
      return 1
  }
}

export const getLevelThresholdRanges = (
  thresholds: number[] = baselineAssessmentThresholds,
): Array<{ from: number; to: number }> => {
  const ranges: Array<{ from: number; to: number }> = []

  for (let i = 0; i < thresholds.length; i++) {
    let from: number
    let to: number

    if (i === 0) {
      from = 0
      to = Math.floor(thresholds[i])
    } else if (i === thresholds.length - 1) {
      from = ranges[i - 1].to + 1
      to = thresholds[i]
    } else {
      from = ranges[i - 1].to + 1
      to = Math.floor(thresholds[i])
    }

    ranges.push({ from, to })
  }

  return ranges
}

export const percentageToLevelType = (
  percentage: number,
  intervals: number,
  thresholds: number[],
): AssessmentLevelType => {
  if (intervals === 3) {
    if (percentage <= thresholds[0]) return 'poor'
    if (percentage <= thresholds[1]) return 'fair'
    return 'good'
  }

  if (percentage <= thresholds[0]) return 'veryPoor'
  if (percentage <= thresholds[1]) return 'poor'
  if (percentage <= thresholds[2]) return 'fair'
  if (percentage <= thresholds[3]) return 'good'
  return 'excellent'
}

export const resolveLevelType = (
  levelsCount: number,
  thresholds: number[],
  value?: number,
  percentage?: number,
): AssessmentLevelType => {
  if (value == null && percentage !== undefined) return percentageToLevelType(percentage, levelsCount, thresholds)
  return valueToLevelType(value, levelsCount)
}

export const getLevelConfig = (
  levelType: AssessmentLevelType,
  theme: Theme,
  formatMessage?: IntlFormatters['formatMessage'],
): AssessmentLevelConfig => {
  return getAssessmentLevelConfig(theme, formatMessage)[levelType]
}

export const getLevelConfigByValue = (
  theme: Theme,
  value?: number,
  levelsCount = 5,
  formatMessage?: IntlFormatters['formatMessage'],
): AssessmentLevelConfig => {
  const levelType = valueToLevelType(value, levelsCount)
  return getLevelConfig(levelType, theme, formatMessage)
}

export const getLevelConfigByPercentage = (
  percentage: number,
  intervals: number,
  theme: Theme,
  thresholds: number[],
  formatMessage?: IntlFormatters['formatMessage'],
): AssessmentLevelConfig => {
  const levelType = percentageToLevelType(percentage, intervals, thresholds)
  return getLevelConfig(levelType, theme, formatMessage)
}
